import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { setOrgTz } from "@/lib/tz";
import { endSession, isTokenExpired, onPublicPage, SESSION_ENDED, tokenExpiry } from "@/lib/session";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gov, setGov] = useState(undefined); // Documents module access; undefined = still loading

  useEffect(() => {
    // Org timezone drives how system/audit timestamps are displayed. Only ask
    // once signed in — this endpoint needs a token, and a 401 here used to bump
    // people off public pages like /reset and /sign.
    const t = localStorage.getItem("token");
    if (!t || isTokenExpired(t)) return;
    api.generalSettings().then((g) => setOrgTz(g.timezone)).catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && isTokenExpired(token)) {
      // Opened after the session ran out: go to sign-in without loading anything.
      if (!onPublicPage()) { endSession("expired"); return; }
      localStorage.removeItem("token");
    }
    if (!localStorage.getItem("token")) {
      setLoading(false);
      return;
    }
    api
      .me()
      .then(setUser)
      .catch(() => localStorage.removeItem("token"))
      .finally(() => setLoading(false));
  }, []);

  // Lock the app the moment the session expires. Timers stall while a laptop
  // sleeps, so also re-check whenever the tab is shown or focused, or restored
  // from the back/forward cache, and follow sign-outs in other tabs.
  useEffect(() => {
    if (!user) return;
    const check = () => {
      if (onPublicPage()) return;
      const token = localStorage.getItem("token");
      if (!token) endSession();
      else if (isTokenExpired(token)) endSession("expired");
    };
    const exp = tokenExpiry(localStorage.getItem("token") || "");
    const timer = exp !== null ? setTimeout(check, Math.min(Math.max(exp - Date.now(), 0) + 1000, 2 ** 31 - 1)) : null;
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    const onStorage = (e) => { if (e.key === "token" || e.key === null) check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    window.addEventListener("pageshow", check);
    window.addEventListener("storage", onStorage);
    check();
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      window.removeEventListener("pageshow", check);
      window.removeEventListener("storage", onStorage);
    };
  }, [user]);

  // Unmount every signed-in page straight away (Protected routes fall back to
  // /login) rather than leaving data on screen while the reload completes.
  useEffect(() => {
    const onEnded = () => setUser(null);
    window.addEventListener(SESSION_ENDED, onEnded);
    return () => window.removeEventListener(SESSION_ENDED, onEnded);
  }, []);

  // The sign-in response doesn't carry permissions; load the full profile.
  const apply = async (res) => {
    localStorage.setItem("token", res.token);
    const me = await api.me().catch(() => res.user);
    setUser(me);
    return me;
  };

  // Returns the raw response so the Login screen can drive the OTP / persona
  // steps. When a token is present, the session is applied immediately.
  const login = async (username, password, persona) => {
    const res = await api.login(username, password, persona);
    if (res.token) return apply(res);
    return res; // { otp_required, challenge_id, ... } or { dual_role, auth_ticket, ... }
  };

  const verifyOtp = async (payload) => {
    const res = await api.loginOtp(payload);
    if (res.token) return apply(res);
    return res; // may be { dual_role, auth_ticket, ... }
  };

  const choosePersona = async (payload) => {
    const res = await api.choosePersona(payload);
    return apply(res);
  };

  const switchPersona = async (persona) => {
    const res = await api.switchPersona(persona);    return apply(res);
  };

  // Management access comes from Management Team membership (server-side), so
  // it can't be derived from roles — fetch it for office users.
  useEffect(() => {
    if (!user) return;
    if (["Support Worker", "Participant Portal"].includes(user.role)) { setGov(null); return; }
    api.govAccess().then(setGov).catch(() => setGov(null));
  }, [user]);
  const refreshGov = () => api.govAccess().then(setGov).catch(() => {});

  const refreshUser = () => api.me().then(setUser).catch(() => {});

  // Admins can change what a role allows at any time; pick that up without a
  // fresh sign-in (the server already enforces it on every request).
  const signedIn = !!user;
  useEffect(() => {
    if (!signedIn) return;
    let last = Date.now();
    const pull = () => {
      if (Date.now() - last < 30000 || !localStorage.getItem("token")) return;
      last = Date.now();
      api.me().then(setUser).catch(() => {});
    };
    const timer = setInterval(pull, 60000);
    window.addEventListener("focus", pull);
    return () => { clearInterval(timer); window.removeEventListener("focus", pull); };
  }, [signedIn]);

  const twofaConfirm = async (payload) => {
    const res = await api.twofaConfirm(payload);
    if (res.token) return apply(res);
    return res;
  };

  const logout = () => endSession();

  // What the user may do comes from their roles' permissions (Admin Settings >
  // Roles & Permissions), e.g. can.has("roster.edit"). Keys: backend/permissions.py.
  const permSet = new Set((user && user.permissions) || []);
  // Menus and pages: usually the same. A Look only support session sees more than it may change.
  const seeSet = new Set((user && (user.can_see || user.permissions)) || []);
  const can = {
    has: (...keys) => keys.some((k) => permSet.has(k)),
    sees: (...keys) => keys.some((k) => seeSet.has(k)),
  };
  // Only the Admin role can manage users and roles.
  const isAdmin = permSet.has("settings.users");

  const isWorker = user?.role === "Support Worker";
  const isPortal = user?.role === "Participant Portal";

  // Office roles that grant desk access (mirrors backend ROLE_MAP keys). A user
  // holding one of these AND "Support Worker" is dual-role and can flip personas.
  const OFFICE_ROLES = new Set([
    "System Manager", "Administrator", "Rostering Admin", "Rostering Manager", "Rostering",
    "Team Leader", "Senior Team Leader", "Standard Office Staff", "HR Manager", "HR User",
    "Accounts Manager", "Accounts User", "Auditor", "Support Team",
    "Incident Manager", "Incident Viewer",
    "Organisation Manager", "Organisation User",
  ]);
  const fr = user?.frappe_roles || [];
  // Backend's authoritative flag (set at login) OR the role-name derivation —
  // the OR keeps the toggle working for sessions on tokens issued before
  // is_dual existed. switchPersona re-verifies against the app-native store.
  const isDual = !!user?.is_dual || (fr.includes("Support Worker") && fr.some((r) => OFFICE_ROLES.has(r)));

  // Documents module. Management = Management or Executive Team members + Admins
  // (drafts, meetings, folders); only Management Team members approve. Team
  // membership isn't a role, so it comes from the server separately.
  can.docs = !!user && !isWorker && !isPortal && can.has("docs.view");
  can.management = can.docs && (isAdmin || !!gov?.is_management);
  can.managementPending = can.docs && !isAdmin && gov === undefined;
  // Executive Team members + Admins (Executive meetings). Exec members also get management access.
  can.executive = can.docs && (isAdmin || !!gov?.is_executive);

  return (
    <AuthCtx.Provider value={{ user, loading, login, verifyOtp, choosePersona, switchPersona, twofaConfirm, logout, can, isAdmin, isWorker, isPortal, isDual, refreshUser, refreshGov }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
